import React, { useState, useRef, useCallback, useEffect } from 'react';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, MapPin, Search, RotateCcw, Maximize2, AlertCircle, Check, Hand, LocateFixed, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['geometry', 'places'];

const SQM_TO_SQFT = 10.7639;

const containerStyle = { width: '100%', height: '440px', borderRadius: '12px' };
const defaultCenter = { lat: 12.9716, lng: 77.5946 };

export default function RooftopTracer({ initialAddress = 'Bangalore', onAreaChange, onPolygonChange, onSegmentDetected }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: LIBRARIES,
    id: 'inpace-google-maps',
  });

  const [center, setCenter] = useState(defaultCenter);
  const [address, setAddress] = useState(initialAddress);
  const [searching, setSearching] = useState(false);
  const [vertices, setVertices] = useState([]);
  const [drawingDone, setDrawingDone] = useState(false);
  const [areaSqft, setAreaSqft] = useState(0);
  const mapRef = useRef(null);

  const onMapLoad = (map) => { mapRef.current = map; };

  useEffect(() => {
    if (!isLoaded || !initialAddress) return;
    geocode(initialAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Detect "lat, lng" pattern — allows users to paste raw coordinates instead of an address
  const parseLatLng = (input) => {
    if (!input) return null;
    // Accept: "12.9716, 77.5946" | "12.9716,77.5946" | "12.9716 77.5946" | with optional whitespace
    const m = String(input).trim().match(/^(-?\d{1,3}(?:\.\d+)?)[ ,]+(-?\d{1,3}(?:\.\d+)?)$/);
    if (!m) return null;
    const lat = parseFloat(m[1]);
    const lng = parseFloat(m[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  };

  const geocode = useCallback(async (addr) => {
    if (!addr || !window.google) return;
    // Fast path — if user typed coordinates, jump directly (no Geocoding API call)
    const coords = parseLatLng(addr);
    if (coords) {
      setCenter(coords);
      if (mapRef.current) {
        mapRef.current.panTo(coords);
        mapRef.current.setZoom(20);
      }
      toast.success(`Jumped to ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`);
      return;
    }
    setSearching(true);
    try {
      const geocoder = new window.google.maps.Geocoder();
      const { results } = await geocoder.geocode({ address: addr + ', India' });
      if (results && results[0]) {
        const loc = results[0].geometry.location;
        const next = { lat: loc.lat(), lng: loc.lng() };
        setCenter(next);
        if (mapRef.current) {
          mapRef.current.panTo(next);
          mapRef.current.setZoom(20);
        }
      } else {
        toast.error('Address not found');
      }
    } catch (e) {
      toast.error(e?.message || 'Geocoding failed. Make sure Geocoding API is enabled.');
    } finally { setSearching(false); }
  }, []);

  const computeAreaFromPath = useCallback((path) => {
    if (!window.google || !window.google.maps?.geometry || path.length < 3) return 0;
    const latLngs = path.map(p => new window.google.maps.LatLng(p.lat, p.lng));
    const sqm = window.google.maps.geometry.spherical.computeArea(latLngs);
    return Math.round(sqm * SQM_TO_SQFT);
  }, []);

  const handleMapClick = (e) => {
    if (drawingDone) return;
    const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setVertices(prev => {
      const next = [...prev, point];
      if (next.length >= 3) setAreaSqft(computeAreaFromPath(next));
      return next;
    });
  };

  const finishDrawing = () => {
    if (vertices.length < 3) {
      toast.error('Click at least 3 corners around the rooftop first');
      return;
    }
    setDrawingDone(true);
    const sqft = computeAreaFromPath(vertices);
    setAreaSqft(sqft);
    if (onAreaChange) onAreaChange(sqft);
    if (onPolygonChange) onPolygonChange(vertices);
    toast.success(`Rooftop traced — ${sqft.toLocaleString('en-IN')} sq.ft`);
  };

  const reset = () => {
    setVertices([]);
    setDrawingDone(false);
    setAreaSqft(0);
    if (onAreaChange) onAreaChange(0);
    if (onPolygonChange) onPolygonChange(null);
  };

  const [locating, setLocating] = useState(false);
  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by this browser');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(next);
        setAddress(`${next.lat.toFixed(6)}, ${next.lng.toFixed(6)}`);
        if (mapRef.current) {
          mapRef.current.panTo(next);
          mapRef.current.setZoom(20);
        }
        toast.success(`Located you at ${next.lat.toFixed(5)}, ${next.lng.toFixed(5)}`);
        setLocating(false);
      },
      (err) => {
        const msg = err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Allow location access in your browser settings.'
          : err.code === err.POSITION_UNAVAILABLE
          ? 'Location unavailable. Try again outdoors or with better GPS signal.'
          : 'Could not get your location. Please try again.';
        toast.error(msg);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Apply a polygon programmatically (used by auto-detect)
  const applyPolygon = (poly, source = 'auto-detected') => {
    if (!poly || poly.length < 3) return;
    setVertices(poly);
    setDrawingDone(true);
    const sqft = computeAreaFromPath(poly);
    setAreaSqft(sqft);
    if (onAreaChange) onAreaChange(sqft);
    if (onPolygonChange) onPolygonChange(poly);
    toast.success(`Roof ${source} — ${sqft.toLocaleString('en-IN')} sq.ft. Click "Clear" to redraw manually.`);
  };

  const [detecting, setDetecting] = useState(false);
  const autoDetectRoof = async () => {
    if (!GOOGLE_MAPS_KEY) { toast.error('Google Maps API key missing'); return; }
    setDetecting(true);
    // Prefer the current map center (user may have panned/zoomed)
    let lat = center.lat, lng = center.lng;
    if (mapRef.current && mapRef.current.getCenter) {
      const c = mapRef.current.getCenter();
      lat = c.lat(); lng = c.lng();
    }
    try {
      // 1) Try Google Solar API — Building Insights
      const url = `https://solar.googleapis.com/v1/buildingInsights:findClosest?location.latitude=${lat}&location.longitude=${lng}&requiredQuality=LOW&key=${GOOGLE_MAPS_KEY}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        // Extract dominant roof segment (largest area) for pitch + azimuth
        const segs = Array.isArray(data?.solarPotential?.roofSegmentStats) ? data.solarPotential.roofSegmentStats : [];
        const dominant = segs
          .slice()
          .sort((a, b) => (b?.stats?.areaMeters2 || 0) - (a?.stats?.areaMeters2 || 0))[0];
        if (dominant && onSegmentDetected) {
          const tilt = Math.round(dominant.pitchDegrees ?? 0);
          const azimuth = Math.round(dominant.azimuthDegrees ?? 180);
          onSegmentDetected({ tilt, azimuth, source: 'solar-api' });
        }
        // Use the top-level building boundingBox for a clean rectangle around the roof
        const bb = data?.boundingBox;
        if (bb && bb.ne && bb.sw) {
          const poly = [
            { lat: bb.ne.latitude, lng: bb.sw.longitude }, // NW
            { lat: bb.ne.latitude, lng: bb.ne.longitude }, // NE
            { lat: bb.sw.latitude, lng: bb.ne.longitude }, // SE
            { lat: bb.sw.latitude, lng: bb.sw.longitude }, // SW
          ];
          if (mapRef.current) {
            mapRef.current.panTo({ lat: (bb.ne.latitude + bb.sw.latitude) / 2, lng: (bb.ne.longitude + bb.sw.longitude) / 2 });
          }
          const extraMsg = dominant ? ` · tilt ${Math.round(dominant.pitchDegrees)}° · azimuth ${Math.round(dominant.azimuthDegrees)}°` : '';
          applyPolygon(poly, `auto-detected via Google Solar API${extraMsg}`);
          setDetecting(false);
          return;
        }
      }
      // 2) Fallback → heuristic 12m × 15m rectangle centered on pin (south-facing)
      const dLat = 15 / 111000; // meters to degrees latitude
      const dLng = 12 / (111000 * Math.cos(lat * Math.PI / 180));
      const poly = [
        { lat: lat + dLat, lng: lng - dLng }, // NW
        { lat: lat + dLat, lng: lng + dLng }, // NE
        { lat: lat - dLat, lng: lng + dLng }, // SE
        { lat: lat - dLat, lng: lng - dLng }, // SW
      ];
      applyPolygon(poly, 'suggested (heuristic rectangle)');
      toast.info('No Solar data for this building — used a 12m × 15m suggested rectangle. Adjust with Clear + redraw if needed.');
    } catch (e) {
      toast.error(e?.message || 'Auto-detect failed');
    } finally {
      setDetecting(false);
    }
  };

  const undoLast = () => {
    if (drawingDone) return;
    setVertices(prev => {
      const next = prev.slice(0, -1);
      setAreaSqft(next.length >= 3 ? computeAreaFromPath(next) : 0);
      return next;
    });
  };

  if (loadError) {
    return (
      <Card className="p-5 border-rose-200 bg-rose-50">
        <div className="flex items-start gap-3 text-rose-800">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">Could not load Google Maps</div>
            <div className="text-sm mt-1">Check the Google Maps API key. Ensure <strong>Maps JavaScript API</strong> + <strong>Geocoding API</strong> are enabled in Google Cloud Console.</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="mb-1.5 block text-xs font-bold uppercase text-emerald-700 tracking-wider">Address or Coordinates</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={address} onChange={e => setAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), geocode(address))}
              className="pl-10" placeholder="e.g. JP Nagar, Bangalore  —  or  12.9716, 77.5946"
              data-testid="tracer-address-input" />
          </div>
        </div>
        <Button onClick={() => geocode(address)} disabled={searching || !isLoaded} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="tracer-locate-btn">
          {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Locate
        </Button>
        <Button onClick={useMyLocation} disabled={locating || !isLoaded} variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="tracer-my-location-btn" title="Use my current GPS location">
          {locating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}
          My location
        </Button>
        <Button onClick={autoDetectRoof} disabled={detecting || !isLoaded} className="bg-amber-500 hover:bg-amber-600 text-emerald-950" data-testid="tracer-auto-detect-btn" title="Auto-detect rooftop using Google Solar API">
          {detecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
          Auto-detect roof
        </Button>
        {vertices.length > 0 && !drawingDone && (
          <>
            <Button variant="outline" onClick={undoLast} className="border-emerald-300 text-emerald-800">Undo last point</Button>
            <Button onClick={finishDrawing} className="bg-amber-500 hover:bg-amber-600 text-emerald-950">
              <Check className="w-4 h-4 mr-2" /> Finish ({vertices.length} pts)
            </Button>
          </>
        )}
        {(vertices.length > 0 || drawingDone) && (
          <Button variant="outline" onClick={reset} className="border-rose-300 text-rose-700">
            <RotateCcw className="w-4 h-4 mr-2" /> Clear
          </Button>
        )}
      </div>

      <div className="rounded-xl overflow-hidden border border-emerald-100 shadow-inner relative">
        {!isLoaded ? (
          <div style={containerStyle} className="flex items-center justify-center bg-emerald-50 text-emerald-700">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading satellite map…
          </div>
        ) : (
          <>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={20}
              onLoad={onMapLoad}
              onClick={handleMapClick}
              options={{
                mapTypeId: 'satellite',
                tilt: 0,
                streetViewControl: false,
                fullscreenControl: true,
                mapTypeControl: true,
                zoomControl: true,
                gestureHandling: 'greedy',
                clickableIcons: false,
              }}
            >
              {vertices.length >= 3 && (
                <Polygon
                  paths={vertices}
                  options={{
                    fillColor: '#10b981', fillOpacity: 0.35,
                    strokeColor: '#fbbf24', strokeWeight: 3,
                    clickable: false,
                  }}
                />
              )}
              {vertices.map((v, i) => (
                <Marker
                  key={i}
                  position={v}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillColor: '#fbbf24',
                    fillOpacity: 1,
                    strokeColor: '#064e3b',
                    strokeWeight: 2,
                  }}
                />
              ))}
            </GoogleMap>
            {!drawingDone && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur rounded-full px-4 py-2 shadow-lg border border-emerald-100 text-xs font-semibold text-emerald-900 flex items-center gap-2 pointer-events-none">
                <Hand className="w-3.5 h-3.5 text-amber-500" />
                {vertices.length === 0 ? 'Click the rooftop corners on the satellite view' :
                 vertices.length < 3 ? `${vertices.length} of 3+ corners — keep clicking` :
                 `${vertices.length} corners — click "Finish" or add more`}
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4 border-emerald-100">
          <div className="text-xs font-bold uppercase text-emerald-700 tracking-wider">Roof Area</div>
          <div className="mt-1 text-2xl font-extrabold text-emerald-900">
            {areaSqft.toLocaleString('en-IN')} <span className="text-sm font-medium text-slate-500">sq.ft</span>
          </div>
        </Card>
        <Card className="p-4 border-emerald-100">
          <div className="text-xs font-bold uppercase text-emerald-700 tracking-wider">Sq. meters</div>
          <div className="mt-1 text-2xl font-extrabold text-emerald-900">
            {Math.round(areaSqft / SQM_TO_SQFT).toLocaleString('en-IN')} <span className="text-sm font-medium text-slate-500">m²</span>
          </div>
        </Card>
        <Card className="p-4 border-emerald-100">
          <div className="text-xs font-bold uppercase text-emerald-700 tracking-wider">Capacity Potential</div>
          <div className="mt-1 text-2xl font-extrabold text-emerald-900">
            ~{Math.floor(areaSqft / 80)} <span className="text-sm font-medium text-slate-500">kW</span>
          </div>
        </Card>
      </div>

      {!drawingDone && (
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900 flex gap-2">
          <Maximize2 className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>How to use:</strong> Search the customer&apos;s address <em>or paste GPS coordinates</em> (e.g. <code className="bg-white/60 px-1 py-0.5 rounded text-[11px]">12.9716, 77.5946</code>), then <strong>click each corner</strong> of the rooftop on the satellite view (use the map&apos;s zoom buttons or scroll-wheel to zoom in). After 3 or more corners, hit <strong>&quot;Finish&quot;</strong> — area &amp; suggested capacity will be applied to the form below.
          </div>
        </div>
      )}
    </div>
  );
}
